import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { shippingApi } from "../../shared/shippingApi";
import type { ShipmentListItem, ShipmentStatus } from "../../types/shipping";

const statuses = [
  { label: "All statuses", value: "" },
  { label: "In progress", value: 1 },
  { label: "Shipped", value: 4 },
  { label: "Ready for pickup", value: 5 },
  { label: "Completed", value: 6 },
  { label: "Void", value: 7 },
];
const pillClass: Record<ShipmentStatus, string> = {
  1: "bg-amber-100 text-amber-800",
  4: "bg-sky-100 text-sky-800",
  5: "bg-cyan-100 text-cyan-800",
  6: "bg-green-100 text-green-800",
  7: "bg-red-100 text-red-800",
};

export default function ManageShippings() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<ShipmentStatus | "">("");
  const [rows, setRows] = useState<ShipmentListItem[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async (search = query, requestedPage = page) => {
    setLoading(true);
    setError("");
    const result = await shippingApi.shipments(search, status, requestedPage);
    if (result.success) {
      setRows(result.data.data || []);
      setPage(result.data.current_page || 1);
      setLastPage(result.data.last_page || 1);
    } else setError(result.data?.message || "Could not load shipments.");
    setLoading(false);
  };
  useEffect(() => {
    load(query, 1);
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const openHref = (row: ShipmentListItem) => `/shippings/${row.id}`;

  return (
    <div className="p-6 space-y-5">
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-primary-600">
              Outbound warehouse
            </p>
            <h1 className="mt-1 text-2xl font-bold text-gray-900">Shipments</h1>
            <p className="mt-1 text-sm text-gray-500">
              Follow every shipment from draft through warehouse execution and completion.
            </p>
          </div>
          <Link
            to="/create-shipping"
            className="rounded-lg bg-main-purple px-4 py-2 text-center font-semibold text-white hover:bg-indigo-600"
          >
            New shipment
          </Link>
        </div>
      </section>
      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm font-medium text-red-700">{error}</p>
      )}
      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex flex-col gap-3 md:flex-row">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              load();
            }}
            className="relative flex-1"
          >
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm"
              placeholder="Shipment, customer, PO, invoice, or seal"
            />
          </form>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value === "" ? "" : Number(event.target.value) as ShipmentStatus)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700"
          >
            {statuses.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-5 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Shipment
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Customer / order
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  References
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quantity
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-500">
                    Loading shipments…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-gray-500">
                    No shipments match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-gray-900">#{row.id}</p>
                      <p className="text-xs text-gray-500">{row.updated_at}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-gray-900">{row.customer || "Customer"}</p>
                      <p className="text-sm text-gray-500">
                        {row.po_number ? `PO ${row.po_number}` : `Order #${row.order_id}`}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      <p>{row.invoice_number ? `Invoice ${row.invoice_number}` : "No invoice"}</p>
                      <p>{row.seal_number ? `Seal ${row.seal_number}` : "No seal"}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-mono tabular-nums text-gray-900">
                        {Number(row.total_quantity || 0).toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </p>
                      <p className="text-xs text-gray-500">{row.line_count} lines</p>
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${pillClass[row.status]}`}
                      >
                        {row.status_label}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Link
                        to={openHref(row)}
                        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                      >
                        {Number(row.status) === 7 ? "View" : "Edit"}
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {lastPage > 1 && (
          <div className="mt-4 flex items-center justify-end gap-3">
            <button
              disabled={page <= 1}
              onClick={() => load(query, page - 1)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {lastPage}
            </span>
            <button
              disabled={page >= lastPage}
              onClick={() => load(query, page + 1)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
